import { flattenObject } from "@global/Utilities/flattenObject";
import { router } from "@inertiajs/vue3";
import { ref } from "vue";
import { useOverviewStore } from "../overviewStore";
import type { MovementProps, SearchDTO } from "../types";
import { getDefinedParams } from "./getDefinedParams";
import { moveMapPosition } from "./moveMapPosition";

type Props = {
    replace?: boolean;
    isMovingMapPosition?: MovementProps;
    preserveScroll?: boolean;
    only?: string[];
    params?: Partial<SearchDTO>;
};

// If a request is aborted, we need to save the saved-properties send with the arguments and use them for the next request.
const savedOnlyProperties = ref<Set<string>>(new Set([]));

// Update the map with the given parameters
export const updateMapPartial = async ({
    isMovingMapPosition,
    preserveScroll,
    replace = true,
    params: { category, ...restParams } = {},
    only = ["search", "markers", "cards"],
}: Props) => {
    const overviewStore = useOverviewStore();

    return new Promise<void>((resolve, reject) => {
        // Determine the URL based on the category
        const url = new URL(window.location.origin);
        switch (category) {
            case 1: url.pathname = "/party"; break;
            case 2: url.pathname = "/meeting"; break;
            case 5: url.pathname = "/activities"; break;
            default: url.pathname = window.location.pathname; break;
        }

        const searchParams = getDefinedParams(flattenObject({ ...getCurrentSearchParams(), ...restParams }));

        // Update the URL with the new search parameters before making the request.
        // Ensure the correct parameters are preserved for the next request in case of a request is aborted.
        if (replace) updateUrlWithFlattenObject(url, searchParams);

        // Add the only properties to the savedOnlyProperties set
        // When a request is aborted, this will ensure that the correct properties are preserved for the next request.
        only.forEach((item) => savedOnlyProperties.value.add(item));

        router.visit(url, {
            only: Array.from(savedOnlyProperties.value),
            preserveScroll,
            preserveState: true,
            showProgress: false,
            replace,
            data: searchParams,
            onStart: () => overviewStore.setIsProcessingMap(true),
            onSuccess: async () => {
                savedOnlyProperties.value.clear();
                if (isMovingMapPosition) moveMapPosition({ params: restParams, animate: isMovingMapPosition.animate });
                resolve();
            },
            onCancel: () => resolve(),
            onFinish: () => overviewStore.setIsProcessingMap(false),
            onError: (error) => reject(error),
        });
    });
};

const updateUrlWithFlattenObject = (url: URL, searchParams: Partial<Domain.Overview.DataTransferObjects.SearchDTO>) => {
    const newURL = new URL(url);
    for (const [key, value] of Object.entries(searchParams)) {
        newURL.searchParams.append(key, String(value));
    }
    window.history.replaceState({}, "", newURL.href);
};

// Get the current search parameters from the URL and make it to nested structure.
// Nested structure is needed, so that the restParams can be merged with the current search parameters.
const getCurrentSearchParams = () => {
    // Returned as searchParams and used in the data argument of the visit method.
    const searchParamsEntries = new URLSearchParams(window.location.search).entries();
    const currentSearchParamsFlat = Object.fromEntries(searchParamsEntries);

    // Convert flat structure with bracket notation to nested object structure
    const currentSearchParams: Record<string, string | Record<string, string>> = {};
    for (const [key, value] of Object.entries(currentSearchParamsFlat)) {
        const matches = key.match(/^([^[]+)(?:\[([^\]]+)\])?$/);
        if (matches) {
            const [, mainKey, nestedKey] = matches;
            if (nestedKey) {
                currentSearchParams[mainKey] = currentSearchParams[mainKey] || {};
                if (typeof currentSearchParams[mainKey] === "object") {
                    currentSearchParams[mainKey][nestedKey] = value;
                }
            } else {
                currentSearchParams[mainKey] = value;
            }
        }
    }

    return currentSearchParams;
};
